import { useId, type Ref } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { JsonValue } from "@thalamos/haven-common";

import { radioValueOf } from "../AssessmentForm/answering.js";

/** Where the latest change to a comment stands: on its way, kept, or not kept. */
export type CommentSave = "saving" | "saved" | "failed";

/**
 * The label of a comment box the assessor can write in, and the language
 * i18next actually used, which differs from `language` where the locale falls
 * back. The evidence of what a respondent was shown must record the label the
 * box showed, so the label is worded nowhere else.
 */
export const itemCommentLabel = (
  t: TFunction,
  language: string,
  {
    commentRequired,
    response,
  }: {
    /** Whether the rules make a comment mandatory for the answer given. */
    commentRequired: boolean;
    /** The answer given, named in the label of a required comment. */
    response: JsonValue | undefined;
  },
): { text: string; language: string } => {
  const { res, usedLng } = commentRequired
    ? t("assessment.item.comment.requiredLabel", {
        value: response === undefined ? undefined : radioValueOf(response),
        lng: language,
        returnDetails: true,
      })
    : t("assessment.item.comment.optionalLabel", {
        lng: language,
        returnDetails: true,
      });

  return { text: res, language: usedLng };
};

/**
 * The comment on one item. Where the rules require one and none is written,
 * the field is highlighted; that is guidance only and never blocks the
 * assessor.
 *
 * Whitespace is not a comment, so a value of only spaces keeps the highlight.
 */
export const ItemComment = ({
  value,
  required,
  response,
  language,
  saveState,
  disabled = false,
  readOnly = false,
  inputRef,
  onChange,
  onRetry,
}: {
  value: string;
  /** Whether the rules make a comment mandatory for the answer given. */
  required: boolean;
  /** The answer given, named in the label of a required comment. */
  response: JsonValue | undefined;
  /**
   * The language to word the label in; the submission's evidence must be
   * worded in the same one, or it records a label other than the one shown.
   */
  language: string;
  /** How the latest change is being saved; undefined before there is one. */
  saveState: CommentSave | undefined;
  disabled?: boolean;
  readOnly?: boolean;
  /** The box written in, for whatever has to move focus into it. */
  inputRef?: Ref<HTMLTextAreaElement>;
  onChange: (value: string) => Promisable<void>;
  onRetry: () => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const messageId = useId();

  if (readOnly) {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" component="div">
          {t("assessment.item.comment.readOnlyLabel")}
        </Typography>
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {value.trim() === "" ? t("assessment.item.noComment") : value}
        </Typography>
      </Box>
    );
  }

  const written = value.trim() !== "";
  const missing = required && !written;
  const label = itemCommentLabel(t, language, {
    commentRequired: required,
    response,
  }).text;
  const placeholder = missing
    ? t("assessment.item.comment.requiredPlaceholder")
    : required
      ? undefined
      : t("assessment.item.comment.optionalPlaceholder");

  return (
    <Box>
      <TextField
        label={label}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        inputRef={inputRef}
        onChange={(event) => {
          void onChange(event.target.value);
        }}
        multiline
        minRows={3}
        fullWidth
        slotProps={{
          htmlInput: { "aria-describedby": messageId },
          inputLabel: { shrink: true },
        }}
        sx={
          missing
            ? {
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                "& .MuiInputLabel-root": { color: "warning.main" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "warning.main",
                  borderWidth: 2,
                },
              }
            : undefined
        }
      />
      <Box id={messageId} aria-live="polite" sx={{ mt: 0.5 }}>
        {saveState === "failed" ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void onRetry();
                }}
              >
                {t("assessment.item.tryAgain")}
              </Button>
            }
          >
            {t("assessment.item.comment.failed")}
          </Alert>
        ) : saveState === "saving" ? (
          <Typography
            variant="body2"
            color={missing ? "warning.main" : "text.secondary"}
          >
            {t("assessment.item.comment.saving")}
          </Typography>
        ) : missing ? (
          <Typography variant="body2" color="warning.main">
            {t("assessment.item.comment.missing")}
          </Typography>
        ) : written && (saveState === "saved" || required) ? (
          <Typography variant="body2" color="text.secondary">
            {t("assessment.item.comment.saved")}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};
